import {Action} from '@reduxjs/toolkit'
import {persistReducer} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import {put, takeLatest, select} from 'redux-saga/effects'
import {UserModel} from '../models/UserModel'
import {getUserByToken} from "./AuthCRUD";

export interface ActionWithPayload<T> extends Action {
  payload?: T
}

export const actionTypes = {
  Login: '[Login] Action',
  Logout: '[Logout] Action',
  Register: '[Register] Action',
  UserRequested: '[Request User] Action',
  UserLoaded: '[Load User] Auth API',
  SetUser: '[Set User] Action',
  SetSessionExpired: '[Set Session Expired] Action',

}

export interface IAuthState {
  user?: UserModel
  accessToken?: string
  refreshToken?: string
  isRememberMe?: boolean
  longTermToken?: string
  displayName?: string
  sessionExpired?: boolean
}

const initialAuthState: IAuthState = {
  user: undefined,
  accessToken: undefined,
  refreshToken: undefined,
  isRememberMe: false,
  longTermToken: undefined,
  displayName: undefined,
  sessionExpired: false,
}

export const reducer = persistReducer(
  {storage, key: 'isg-next-auth', whitelist: ['user', 'accessToken', 'isRememberMe', 'longTermToken', 'displayName']},
  (state: IAuthState = initialAuthState, action: ActionWithPayload<IAuthState>) => {
    switch (action.type) {
      case actionTypes.Login: {
        const accessToken = action.payload?.accessToken
        const refreshToken = action.payload?.refreshToken
        const isRememberMe = action.payload?.isRememberMe ?? false
        const longTermToken = action.payload?.longTermToken
        const displayName = action.payload?.displayName

        if (refreshToken) {
          sessionStorage.setItem('refresh-token', refreshToken)
        } else {
          sessionStorage.removeItem('refresh-token')
        }

        if (isRememberMe && longTermToken) {
          localStorage.setItem(
            'remember-me',
            JSON.stringify({
              token: longTermToken,
              refreshToken,
              displayName,
              longTermToken,
            })
          )
        } else {
          localStorage.removeItem('remember-me')
        }

        return {accessToken, refreshToken, user: undefined, isRememberMe, longTermToken, displayName, sessionExpired: false}
      }

      case actionTypes.Register: {
        const accessToken = action.payload?.accessToken
        return {accessToken, refreshToken: undefined, user: undefined, isRememberMe: false, sessionExpired: false}
      }

      case actionTypes.Logout: {
        sessionStorage.removeItem('refresh-token')
        localStorage.removeItem('remember-me')
        return initialAuthState
      }

      case actionTypes.UserLoaded: {
        const user = action.payload?.user
        const isRememberMe = action.payload?.isRememberMe ?? state.isRememberMe
        return {...state, user, isRememberMe, sessionExpired: false}
      }

      case actionTypes.SetUser: {
        const user = action.payload?.user ?? state.user
        const accessToken = action.payload?.accessToken ?? state.accessToken
        const refreshToken = action.payload?.refreshToken ?? state.refreshToken
        const isRememberMe = action.payload?.isRememberMe ?? state.isRememberMe
        const longTermToken = action.payload?.longTermToken ?? state.longTermToken
        const displayName = action.payload?.displayName ?? state.displayName

        if (refreshToken) {
          sessionStorage.setItem('refresh-token', refreshToken)
        } else {
          sessionStorage.removeItem('refresh-token')
        }

        if (isRememberMe && longTermToken) {
          localStorage.setItem(
            'remember-me',
            JSON.stringify({
              token: longTermToken,
              refreshToken,
              displayName,
              longTermToken,
            })
          )
        } else if (!isRememberMe) {
          localStorage.removeItem('remember-me')
        }

        return {...state, user, accessToken, refreshToken, isRememberMe, longTermToken, displayName, sessionExpired: false}
      }

      case actionTypes.SetSessionExpired: {
        const sessionExpired = action.payload?.sessionExpired ?? false
        return {...state, sessionExpired}
      }

      default:
        return state
    }
  }
)

export const actions = {
  login: (
    accessToken: string,
    refreshToken: string,
    isRememberMe: boolean,
    longTermToken?: string,
    displayName?: string
  ) => ({
    type: actionTypes.Login,
    payload: {accessToken, refreshToken, isRememberMe, longTermToken, displayName},
  }),
  register: (accessToken: string) => ({
    type: actionTypes.Register,
    payload: {accessToken},
  }),
  logout: () => ({type: actionTypes.Logout}),
  requestUser: () => ({
    type: actionTypes.UserRequested,
  }),
  fulfillUser: (user: UserModel, isRememberMe?: boolean) => ({
    type: actionTypes.UserLoaded,
    payload: {user, isRememberMe},
  }),
  setUser: (
    user?: UserModel,
    accessToken?: string,
    refreshToken?: string,
    isRememberMe?: boolean,
    longTermToken?: string,
    displayName?: string
  ) => ({
    type: actionTypes.SetUser,
    payload: {user, accessToken, refreshToken, isRememberMe, longTermToken, displayName},
  }),
  setSessionExpired: (sessionExpired: boolean) => ({
    type: actionTypes.SetSessionExpired,
    payload: {sessionExpired},
  }),

  store: () => ({type: "def"}),
}

export function* saga() {
  yield takeLatest(actionTypes.Login, function* loginSaga() {
    yield put(actions.requestUser())
  })

  yield takeLatest(actionTypes.Register, function* registerSaga() {
    yield put(actions.requestUser())
  })

  yield takeLatest(actionTypes.UserRequested, function* userRequested() {
    // @ts-ignore
    const getToken = (state) => state.auth.accessToken;
    // @ts-ignore
    let token = yield select(getToken)
    const { data: user } = yield getUserByToken(token)
    yield put(actions.fulfillUser(user))
  })
}
